import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Badge, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import StreamService from '../../../services/StreamService';
import VodService from '../../../services/VodService';

const CaptionManager = ({ show, onHide, itemId, itemType = 'stream' }) => {
  const [captions, setCaptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newCaption, setNewCaption] = useState({
    language: '',
    label: '',
    url: '',
    isDefault: false
  });

  const commonLanguages = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Spanish' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' },
    { code: 'it', label: 'Italian' },
    { code: 'pt', label: 'Portuguese' },
    { code: 'ru', label: 'Russian' },
    { code: 'zh', label: 'Chinese' },
    { code: 'ja', label: 'Japanese' },
    { code: 'ko', label: 'Korean' },
    { code: 'ar', label: 'Arabic' },
    { code: 'hi', label: 'Hindi' },
    { code: 'ta', label: 'Tamil' },
    { code: 'te', label: 'Telugu' },
    { code: 'bn', label: 'Bengali' },
    { code: 'ur', label: 'Urdu' }
  ];

  useEffect(() => {
    if (show && itemId) {
      loadCaptions();
    }
  }, [show, itemId]);

  const loadCaptions = async () => {
    setLoading(true);
    try {
      let response;
      if (itemType === 'stream') {
        response = await StreamService.getCaptions(itemId);
      } else {
        response = await VodService.getCaptions(itemId);
      }
      
      if (response.data?.success) {
        setCaptions(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading captions:', error);
      toast.error('Failed to load captions');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCaption = async (e) => {
    e.preventDefault();
    
    if (!newCaption.language || !newCaption.label || !newCaption.url) {
      toast.error('Please fill in all fields');
      return;
    }

    // Validate URL format
    if (!newCaption.url.match(/^https?:\/\/.+/)) {
      toast.error('Please enter a valid URL starting with http:// or https://');
      return;
    }

    setSaving(true);
    try {
      let response;
      if (itemType === 'stream') {
        response = await StreamService.addCaption(itemId, newCaption);
      } else {
        response = await VodService.addCaption(itemId, newCaption);
      }

      if (response.data?.success) {
        toast.success('Caption added successfully');
        setNewCaption({ language: '', label: '', url: '', isDefault: false });
        loadCaptions();
      }
    } catch (error) {
      console.error('Error adding caption:', error);
      toast.error('Failed to add caption');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveCaption = async (language) => {
    if (!window.confirm('Are you sure you want to remove this caption?')) {
      return;
    }

    try {
      let response;
      if (itemType === 'stream') {
        response = await StreamService.removeCaption(itemId, language);
      } else {
        response = await VodService.removeCaption(itemId, language);
      }

      if (response.data?.success) {
        toast.success('Caption removed successfully');
        loadCaptions();
      }
    } catch (error) {
      console.error('Error removing caption:', error);
      toast.error('Failed to remove caption');
    }
  };

  const handleSetDefault = async (language) => {
    try {
      let response;
      if (itemType === 'stream') {
        response = await StreamService.setDefaultCaption(itemId, language);
      } else {
        response = await VodService.setDefaultCaption(itemId, language);
      }

      if (response.data?.success) {
        toast.success('Default caption set successfully');
        loadCaptions();
      }
    } catch (error) {
      console.error('Error setting default caption:', error);
      toast.error('Failed to set default caption');
    }
  };

  const handleLanguageChange = (e) => {
    const selectedLang = commonLanguages.find(l => l.code === e.target.value);
    setNewCaption({
      ...newCaption,
      language: e.target.value,
      label: selectedLang ? selectedLang.label : newCaption.label
    });
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-closed-captioning me-2"></i>
          Manage Captions
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          <i className="fas fa-info-circle me-2"></i>
          Add caption tracks in WebVTT (.vtt) or SRT (.srt) format. 
          Captions must be hosted on a publicly accessible URL (CORS-enabled).
        </Alert>

        {/* Add New Caption Form */}
        <Form onSubmit={handleAddCaption} className="mb-4">
          <h6 className="mb-3">Add New Caption Track</h6>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-2">
                <Form.Label>Language</Form.Label>
                <Form.Select
                  value={newCaption.language}
                  onChange={handleLanguageChange}
                  required
                >
                  <option value="">Select...</option>
                  {commonLanguages.map(lang => (
                    <option key={lang.code} value={lang.code}>
                      {lang.label} ({lang.code})
                    </option>
                  ))}
                  <option value="custom">Custom...</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-2">
                <Form.Label>Display Label</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., English (US)"
                  value={newCaption.label}
                  onChange={(e) => setNewCaption({...newCaption, label: e.target.value})}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-2">
                <Form.Label>Caption URL</Form.Label>
                <Form.Control
                  type="url"
                  placeholder="https://example.com/captions.vtt"
                  value={newCaption.url}
                  onChange={(e) => setNewCaption({...newCaption, url: e.target.value})}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={1}>
              <Form.Group className="mb-2">
                <Form.Label>Default</Form.Label>
                <Form.Check
                  type="checkbox"
                  checked={newCaption.isDefault}
                  onChange={(e) => setNewCaption({...newCaption, isDefault: e.target.checked})}
                  className="mt-2"
                />
              </Form.Group>
            </Col>
          </Row>
          <div className="d-flex justify-content-between align-items-center">
            <Form.Text className="text-muted">
              Supported formats: WebVTT (.vtt), SRT (.srt)
            </Form.Text>
            <Button 
              type="submit" 
              variant="primary" 
              size="sm"
              disabled={saving}
            >
              {saving ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <><i className="fas fa-plus me-1"></i> Add Caption</>
              )}
            </Button>
          </div>
        </Form>

        {/* Caption List */}
        <h6 className="mb-3">Existing Caption Tracks</h6>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
          </div>
        ) : captions.length === 0 ? (
          <Alert variant="light" className="text-center">
            <i className="fas fa-inbox fa-2x mb-2 text-muted"></i>
            <p className="mb-0 text-muted">No caption tracks added yet</p>
          </Alert>
        ) : (
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>Language</th>
                <th>Label</th>
                <th>URL</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {captions.map((caption, index) => (
                <tr key={index}>
                  <td>
                    <Badge bg="secondary">{caption.language}</Badge>
                  </td>
                  <td>{caption.label}</td>
                  <td>
                    <a 
                      href={caption.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-truncate d-inline-block"
                      style={{ maxWidth: '200px' }}
                      title={caption.url}
                    >
                      {caption.url}
                    </a>
                  </td>
                  <td>
                    {caption.isDefault ? (
                      <Badge bg="success">Default</Badge>
                    ) : (
                      <Badge bg="light" text="dark">Optional</Badge>
                    )}
                  </td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-1"
                      onClick={() => handleSetDefault(caption.language)}
                      disabled={caption.isDefault}
                      title="Set as default"
                    >
                      <i className="fas fa-star"></i>
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveCaption(caption.language)}
                      title="Remove caption"
                    >
                      <i className="fas fa-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CaptionManager;
